import {
  CUSTOM_TRACK_SCHEMA_VERSION,
  normalizeCustomTrackDefinition,
  normalizeCustomTrackId,
  type CustomTrackDefinition,
  type CustomTrackId,
} from "../game/simulation/customTrackSchema";

/** GET may contain the whole bounded store; mutations still use a 64 KiB body server limit. */
export const SERVER_TRACKS_MAX_RESPONSE_BYTES = 2 * 1024 * 1024;
export const EDITOR_LOCAL_DRAFT_MAX_BYTES = 64 * 1024;
export const SERVER_TRACKS_PATH = "/api/admin/tracks" as const;
export const SERVER_ADMIN_ME_PATH = "/api/admin/me" as const;
export const EDITOR_LOCAL_DRAFT_STORAGE_KEY = "beerkart-v2:editor-local-draft-v1" as const;
export const EDITOR_LOCAL_DRAFT_SCHEMA_VERSION = 1 as const;

const MAX_TRACKS = 100;
const MAX_METRIC_KEYS = 32;
const MAX_METRIC_TEXT = 160;

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ServerTrackEntry {
  readonly schemaVersion: typeof CUSTOM_TRACK_SCHEMA_VERSION;
  readonly key: CustomTrackId;
  readonly revision: number;
  readonly def: CustomTrackDefinition;
}

export interface ServerTrackMetrics {
  readonly [key: string]: unknown;
}

export interface OperatorCapability {
  readonly enabled: boolean;
  readonly authenticated: boolean;
  readonly authorized: boolean;
}

export interface ServerTrackMutation {
  readonly track: ServerTrackEntry;
  readonly metrics: ServerTrackMetrics;
}

export type ServerTracksErrorKind =
  | "unauthorized"
  | "forbidden"
  | "conflict"
  | "admission-denied"
  | "busy"
  | "unavailable"
  | "validation"
  | "network"
  | "protocol";

export class ServerTracksClientError extends Error {
  constructor(
    readonly kind: ServerTracksErrorKind,
    message: string,
    readonly status = 0,
    readonly current?: ServerTrackEntry,
    readonly metrics?: ServerTrackMetrics,
  ) {
    super(message);
    this.name = "ServerTracksClientError";
  }
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasOwn(record: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function requireRecord(value: unknown, label: string): Record<string, unknown> {
  if (!isPlainRecord(value)) throw new ServerTracksClientError("protocol", `${label} was not a JSON object`);
  return value;
}

function exactKeys(record: Record<string, unknown>, required: readonly string[], optional: readonly string[] = []): void {
  const allowed = new Set([...required, ...optional]);
  for (const key of Object.keys(record)) {
    if (!allowed.has(key)) throw new ServerTracksClientError("protocol", `Server response contains unknown field ${key}`);
  }
  for (const key of required) {
    if (!hasOwn(record, key)) throw new ServerTracksClientError("protocol", `Server response is missing ${key}`);
  }
}

function positiveRevision(value: unknown, label: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 1) {
    throw new ServerTracksClientError("protocol", `${label} is not a positive safe integer`);
  }
  return value as number;
}

function normalizeServerTrack(value: unknown): ServerTrackEntry {
  const record = requireRecord(value, "track");
  exactKeys(record, ["schemaVersion", "key", "revision", "def"]);
  if (record.schemaVersion !== CUSTOM_TRACK_SCHEMA_VERSION) {
    throw new ServerTracksClientError("protocol", "Server track schema version is unsupported");
  }
  let key: CustomTrackId;
  try { key = normalizeCustomTrackId(record.key); }
  catch { throw new ServerTracksClientError("protocol", "Server returned an invalid track identifier"); }
  const revision = positiveRevision(record.revision, "track revision");
  let def: CustomTrackDefinition;
  try {
    def = normalizeCustomTrackDefinition(record.def);
  } catch {
    throw new ServerTracksClientError("protocol", "Server returned an invalid track definition");
  }
  return { schemaVersion: CUSTOM_TRACK_SCHEMA_VERSION, key, revision, def };
}

function normalizeMetrics(value: unknown): ServerTrackMetrics {
  if (!isPlainRecord(value)) throw new ServerTracksClientError("protocol", "Server admission metrics were invalid");
  const keys = Object.keys(value);
  if (keys.length > MAX_METRIC_KEYS) throw new ServerTracksClientError("protocol", "Server admission metrics were too large");
  const metrics: Record<string, unknown> = {};
  for (const key of keys) {
    const metric = value[key];
    if (typeof metric === "string" && metric.length > MAX_METRIC_TEXT) {
      throw new ServerTracksClientError("protocol", "Server admission metrics were too large");
    }
    if (typeof metric === "number" && !Number.isFinite(metric)) {
      throw new ServerTracksClientError("protocol", "Server admission metrics contained a non-finite number");
    }
    if (metric !== null && typeof metric !== "string" && typeof metric !== "number" && typeof metric !== "boolean") {
      throw new ServerTracksClientError("protocol", "Server admission metrics contained an unsupported value");
    }
    metrics[key] = metric;
  }
  return metrics;
}

function parseCapability(value: unknown): OperatorCapability {
  const record = requireRecord(value, "operator capability");
  exactKeys(record, ["ok", "enabled", "authenticated", "authorized"]);
  if (record.ok !== true || typeof record.enabled !== "boolean" || typeof record.authenticated !== "boolean" || typeof record.authorized !== "boolean") {
    throw new ServerTracksClientError("protocol", "Operator capability response was invalid");
  }
  return { enabled: record.enabled, authenticated: record.authenticated, authorized: record.authorized };
}

function parseTrackList(value: unknown): readonly ServerTrackEntry[] {
  const record = requireRecord(value, "track list");
  exactKeys(record, ["ok", "tracks"]);
  if (record.ok !== true || !Array.isArray(record.tracks) || record.tracks.length > MAX_TRACKS) {
    throw new ServerTracksClientError("protocol", "Server track list response was invalid");
  }
  return record.tracks.map(normalizeServerTrack);
}

function errorKind(status: number, message: string): ServerTracksErrorKind {
  if (status === 401) return "unauthorized";
  if (status === 403) return "forbidden";
  if (status === 409) return "conflict";
  if (status === 422) return "admission-denied";
  if (status === 429) return "busy";
  if (status >= 500) return "unavailable";
  if (status >= 400) return "validation";
  return message ? "protocol" : "network";
}

function parseErrorPayload(value: unknown, status: number): ServerTracksClientError {
  const record = requireRecord(value, "server error");
  exactKeys(record, ["ok", "error"], ["current", "metrics"]);
  if (record.ok !== false || typeof record.error !== "string" || !record.error.trim()) {
    throw new ServerTracksClientError("protocol", "Server error response was invalid", status);
  }
  const current = record.current === undefined ? undefined : normalizeServerTrack(record.current);
  const metrics = record.metrics === undefined ? undefined : normalizeMetrics(record.metrics);
  return new ServerTracksClientError(errorKind(status, record.error), record.error, status, current, metrics);
}

function contentTypeIsJson(response: Response): boolean {
  const header = response.headers.get("content-type");
  return typeof header === "string" && header.split(";", 1)[0].trim().toLowerCase() === "application/json";
}

async function boundedResponseText(response: Response): Promise<string> {
  const tooLarge = () => new ServerTracksClientError("protocol", "Server response exceeded the editor size limit", response.status);
  const declaredLength = response.headers.get("content-length");
  if (declaredLength && /^\d+$/.test(declaredLength) && Number(declaredLength) > SERVER_TRACKS_MAX_RESPONSE_BYTES) {
    await response.body?.cancel().catch(() => undefined);
    throw tooLarge();
  }
  if (!response.body) return "";
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let bytes = 0, text = "";
  try {
    for (;;) {
      const chunk = await reader.read();
      if (chunk.done) break;
      bytes += chunk.value.byteLength;
      if (bytes > SERVER_TRACKS_MAX_RESPONSE_BYTES) {
        await reader.cancel().catch(() => undefined);
        throw tooLarge();
      }
      text += decoder.decode(chunk.value, { stream: true });
    }
    return text + decoder.decode();
  } finally {
    reader.releaseLock();
  }
}

export type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/** Strict, same-origin browser client for the operator custom-track API. */
export class ServerTracksClient {
  private readonly fetchImpl: FetchLike;
  private readonly tracksPath: string;
  private readonly capabilityPath: string;

  constructor(fetchImpl: FetchLike = (input, init) => fetch(input, init), basePath = "") {
    this.fetchImpl = fetchImpl;
    this.tracksPath = `${basePath}${SERVER_TRACKS_PATH}`;
    this.capabilityPath = `${basePath}${SERVER_ADMIN_ME_PATH}`;
  }

  async capability(): Promise<OperatorCapability> {
    const value = await this.requestJson(this.capabilityPath, { method: "GET" });
    return parseCapability(value);
  }

  async list(): Promise<readonly ServerTrackEntry[]> {
    const value = await this.requestJson(this.tracksPath, { method: "GET" });
    return parseTrackList(value);
  }

  async create(key: unknown, definition: unknown): Promise<ServerTrackMutation> {
    return this.mutate("POST", key, definition, 0);
  }

  async update(key: unknown, definition: unknown, expectedRevision: unknown): Promise<ServerTrackMutation> {
    return this.mutate("PUT", key, definition, expectedRevision);
  }

  async delete(key: unknown, expectedRevision: unknown): Promise<CustomTrackId> {
    const normalizedKey = this.normalizeKey(key);
    const revision = this.normalizeExpectedRevision(expectedRevision, false);
    const value = await this.requestJson(this.tracksPath, {
      method: "DELETE",
      body: JSON.stringify({ key: normalizedKey, expectedRevision: revision }),
    });
    const record = requireRecord(value, "delete response");
    exactKeys(record, ["ok", "key"]);
    let returnedKey: CustomTrackId;
    try { returnedKey = normalizeCustomTrackId(record.key); }
    catch { throw new ServerTracksClientError("protocol", "Server delete response contained an invalid identifier"); }
    if (record.ok !== true || returnedKey !== normalizedKey) {
      throw new ServerTracksClientError("protocol", "Server delete response identity did not match the request");
    }
    return normalizedKey;
  }

  private async mutate(method: "POST" | "PUT", key: unknown, definition: unknown, expectedRevision: unknown): Promise<ServerTrackMutation> {
    const normalizedKey = this.normalizeKey(key);
    const normalizedDefinition = this.normalizeDefinition(definition);
    const revision = this.normalizeExpectedRevision(expectedRevision, method === "POST");
    const value = await this.requestJson(this.tracksPath, {
      method,
      body: JSON.stringify({ key: normalizedKey, def: normalizedDefinition, expectedRevision: revision }),
    });
    const record = requireRecord(value, "save response");
    exactKeys(record, ["ok", "track"], ["metrics"]);
    if (record.ok !== true) throw new ServerTracksClientError("protocol", "Server save response was invalid");
    const track = normalizeServerTrack(record.track);
    if (track.key !== normalizedKey) throw new ServerTracksClientError("protocol", "Server save response identity did not match the request");
    const metrics = record.metrics === undefined ? {} : normalizeMetrics(record.metrics);
    return { track, metrics };
  }

  private normalizeKey(value: unknown): CustomTrackId {
    try { return normalizeCustomTrackId(value); }
    catch { throw new ServerTracksClientError("validation", "Track key must be a lowercase c_ identifier"); }
  }

  private normalizeDefinition(value: unknown): CustomTrackDefinition {
    try { return normalizeCustomTrackDefinition(value); }
    catch { throw new ServerTracksClientError("validation", "Track definition is invalid"); }
  }

  private normalizeExpectedRevision(value: unknown, allowZero: boolean): number {
    if (!Number.isSafeInteger(value) || (value as number) < (allowZero ? 0 : 1)) {
      throw new ServerTracksClientError("validation", `Expected revision must be a ${allowZero ? "non-negative" : "positive"} safe integer`);
    }
    if (!allowZero && value === 0) throw new ServerTracksClientError("validation", "Expected revision must be positive");
    return value as number;
  }

  private async requestJson(path: string, init: RequestInit): Promise<unknown> {
    let response: Response;
    try {
      response = await this.fetchImpl(path, {
        ...init,
        credentials: "same-origin",
        cache: "no-store",
        headers: { Accept: "application/json", "Content-Type": "application/json", ...(init.headers ?? {}) },
      });
    } catch {
      throw new ServerTracksClientError("network", "Server could not be reached");
    }
    if (!contentTypeIsJson(response)) {
      // Vite's HTML fallback is deliberately never interpreted as a valid API response.
      throw new ServerTracksClientError("protocol", "Server returned HTML or a non-JSON response", response.status);
    }
    let text: string;
    try { text = await boundedResponseText(response); }
    catch (error) {
      if (error instanceof ServerTracksClientError) throw error;
      throw new ServerTracksClientError("network", "Server response could not be read", response.status);
    }
    let value: unknown;
    try { value = JSON.parse(text); }
    catch { throw new ServerTracksClientError("protocol", "Server returned malformed JSON", response.status); }
    if (!response.ok) throw parseErrorPayload(value, response.status);
    return value;
  }
}

export interface LocalDraftRead {
  readonly status: "missing" | "valid" | "invalid" | "unavailable";
  readonly definition?: CustomTrackDefinition;
  readonly key?: CustomTrackId;
  readonly message?: string;
}

function localDraftEnvelope(definition: unknown, key?: unknown): string {
  const normalized = normalizeCustomTrackDefinition(definition);
  const normalizedKey = key === undefined || key === "" ? undefined : normalizeCustomTrackId(key);
  const serialized = JSON.stringify(normalizedKey
    ? { schemaVersion: EDITOR_LOCAL_DRAFT_SCHEMA_VERSION, key: normalizedKey, def: normalized }
    : { schemaVersion: EDITOR_LOCAL_DRAFT_SCHEMA_VERSION, def: normalized });
  if (serialized.length > EDITOR_LOCAL_DRAFT_MAX_BYTES) throw new Error("Local draft exceeds the editor size limit");
  return serialized;
}

/** Read without deleting malformed data; the operator can still export/recover it manually. */
export function readLocalDraft(storage: StorageLike): LocalDraftRead {
  let serialized: string | null;
  try { serialized = storage.getItem(EDITOR_LOCAL_DRAFT_STORAGE_KEY); }
  catch { return { status: "unavailable", message: "Local draft storage is unavailable" }; }
  if (serialized === null) return { status: "missing" };
  if (serialized.length > EDITOR_LOCAL_DRAFT_MAX_BYTES) {
    return { status: "invalid", message: "Saved local draft exceeds the editor size limit; it was left untouched." };
  }
  try {
    const value: unknown = JSON.parse(serialized);
    const record = requireRecord(value, "local draft");
    exactKeys(record, ["schemaVersion", "def"], ["key"]);
    if (record.schemaVersion !== EDITOR_LOCAL_DRAFT_SCHEMA_VERSION) throw new Error("unsupported local draft version");
    const key = record.key === undefined ? undefined : normalizeCustomTrackId(record.key);
    return { status: "valid", definition: normalizeCustomTrackDefinition(record.def), key };
  } catch (error) {
    return { status: "invalid", message: `Saved local draft is invalid and was left untouched${error instanceof Error ? `: ${error.message}` : "."}` };
  }
}

export function writeLocalDraft(storage: StorageLike, definition: unknown, key?: unknown): boolean {
  const serialized = localDraftEnvelope(definition, key);
  try { storage.setItem(EDITOR_LOCAL_DRAFT_STORAGE_KEY, serialized); return true; }
  catch { return false; }
}

/** Clear only a valid matching record; malformed or newer records are preserved. */
export function clearLocalDraftIfMatches(storage: StorageLike, definition: unknown, key?: unknown): boolean {
  let current: LocalDraftRead;
  try { current = readLocalDraft(storage); }
  catch { return false; }
  if (current.status !== "valid" || !current.definition) return false;
  let expected: CustomTrackDefinition;
  try { expected = normalizeCustomTrackDefinition(definition); }
  catch { return false; }
  if (JSON.stringify(current.definition) !== JSON.stringify(expected)) return false;
  if (key !== undefined && current.key !== key) return false;
  try { storage.removeItem(EDITOR_LOCAL_DRAFT_STORAGE_KEY); return true; }
  catch { return false; }
}
